import React, { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Clock3, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

export const DigestDefaultMigrationCard = () => {
  const queryClient = useQueryClient();
  const [confirm, setConfirm] = useState("");
  const [result, setResult] = useState(null);

  const { data, isLoading, isError } = useQuery({
    queryKey: ["admin-digest-migration"],
    queryFn: async () => (await api.get("/admin/digest-default-migration")).data,
    retry: false,
  });

  const apply = useMutation({
    mutationFn: async () =>
      (await api.post("/admin/digest-default-migration/apply", { confirm: "APPLY" })).data,
    onSuccess: (res) => {
      setResult(res);
      setConfirm("");
      toast.success(`Migration applied — ${res.reset.length} reset · run ${res.run_id}`);
      queryClient.invalidateQueries({ queryKey: ["admin-digest-migration"] });
    },
    onError: (e) =>
      toast.error(e?.response?.data?.detail || "Could not run the migration."),
  });

  if (isError) return null;

  return (
    <Card className="border-0 p-6" data-testid="admin-digest-migration-card">
      <div className="flex items-start gap-3">
        <Clock3 className="mt-0.5 h-5 w-5 text-primary" />
        <div>
          <h2 className="text-base md:text-lg font-[640] text-foreground">
            Digest schedule — legacy 5:00 PM default (OAD-142)
          </h2>
          <p className="mt-1 text-sm text-muted-foreground">
            Clears a stored <code>["17:00"]</code> that was never deliberately chosen, so those
            members inherit the recommended schedule. Deliberate and custom schedules are kept;
            opt-outs, pauses and time zones are untouched. Safe to re-run.
          </p>
        </div>
      </div>

      {isLoading ? (
        <p className="mt-4 text-sm text-muted-foreground">Checking…</p>
      ) : (
        <dl className="mt-4 grid grid-cols-2 gap-3 text-sm sm:grid-cols-3"
            data-testid="admin-digest-migration-counts">
          <Row label="Preference records" value={data.records_total} testId="records-total" />
          <Row label="Already inherited" value={data.inherited_no_field} testId="inherited" />
          <Row label="Eligible (stored)" value={data.eligible} testId="eligible" />
          <Row label="Legacy → reset" value={data.to_reset.length} testId="to-reset" />
          <Row label="Kept: deliberate" value={data.preserved_deliberate.length} testId="deliberate" />
          <Row label="Kept: custom" value={data.preserved_custom.length} testId="custom" />
          <Row label="Malformed" value={data.malformed.length} testId="malformed" />
        </dl>
      )}

      <div className="mt-5 flex flex-col gap-3 sm:flex-row sm:items-center">
        <Input
          value={confirm}
          onChange={(e) => setConfirm(e.target.value)}
          placeholder='Type APPLY to confirm'
          className="sm:max-w-[220px]"
          data-testid="admin-digest-migration-confirm"
        />
        <Button
          onClick={() => apply.mutate()}
          disabled={confirm !== "APPLY" || apply.isPending}
          data-testid="admin-digest-migration-apply"
        >
          {apply.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          Run migration
        </Button>
      </div>

      {result && (
        <div className="mt-4 rounded-lg bg-secondary p-4 text-sm" data-testid="admin-digest-migration-result">
          <p className="font-[600] text-foreground">
            Reset {result.reset.length} · skipped {result.skipped_concurrent_change.length} ·
            kept {result.preserved_deliberate + result.preserved_custom}
          </p>
          <p className="mt-1 text-muted-foreground">
            run_id <span data-testid="admin-digest-migration-run-id">{result.run_id}</span> — keep
            this to roll back.
          </p>
        </div>
      )}
    </Card>
  );
};

const Row = ({ label, value, testId }) => (
  <div data-testid={`admin-digest-migration-${testId}`}>
    <dt className="text-xs uppercase tracking-wide text-muted-foreground">{label}</dt>
    <dd className="text-lg font-[640] text-foreground">{value}</dd>
  </div>
);
